import type { ChatHomeTile } from "../home-tiles.js";

export type ChatHomeTilesScope = {
  country: string;
  product: string;
  culture: string;
  countryLabel: string;
  helpCenterLabel: string;
  productLabel: string;
  productShortLabel: string;
  cultureLabel: string;
  cultureShortLabel: string;
  languageId: number;
  searchArea: string;
  isDefaultProductForCountry: boolean;
  isDefaultCulture: boolean;
  isPublic: boolean;
};

export type ChatHomeTilesPlacementOption = {
  country: string;
  product: string;
  countryLabel: string;
  helpCenterLabel: string;
  productLabel: string;
  productShortLabel: string;
  searchArea: string;
  supportedCultures: Array<{
    culture: string;
    label: string;
    shortLabel: string;
    languageId: number;
    isDefault: boolean;
  }>;
};

export type ChatHomeTilesConfig = {
  scopes: ChatHomeTilesScope[];
  placements: ChatHomeTilesPlacementOption[];
  initialScope: ChatHomeTilesScope;
  maxPublicTiles: number;
};

export type ChatHomeTilePlacement = {
  id: number;
  country: string;
  product: string;
  sorting: number;
  isActive: boolean;
  placementJson: string;
  createdUtc: string;
  updatedUtc: string;
};

export type ChatHomeTileCmsTile = {
  id: number;
  contentJson: string;
  title: string;
  description: string;
  question: string;
  createdUtc: string;
  updatedUtc: string;
  placements: ChatHomeTilePlacement[];
};

export type ChatHomeTilesResponse = {
  tiles: ChatHomeTileCmsTile[];
  previewTiles: ChatHomeTile[];
  version: string;
};

export type ChatHomeTileSaveRequest = {
  id?: number;
  contentJson: string;
  // The UpdatedUtc the editor last loaded. Sent on updates so the server can reject a
  // stale overwrite (HTTP 409). Omitted to force a last-write-wins overwrite.
  expectedUpdatedUtc?: string;
  placements: Array<{
    country: string;
    product: string;
    sorting: number;
    isActive: boolean;
    placementJson?: string;
  }>;
};

type FetchFunction = typeof fetch;

export class ChatHomeTilesApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly errors: string[] = [],
  ) {
    super(message);
    this.name = "ChatHomeTilesApiError";
  }
}

export class ChatHomeTilesApiClient {
  // The default wraps the global `fetch` so it is always invoked with the
  // correct `this` (window). Passing the bare `fetch` reference and calling it
  // as `this.fetchFunction(...)` would invoke it with `this` bound to this
  // client instance, which browsers reject with "Illegal invocation".
  constructor(
    private readonly fetchFunction: FetchFunction = (input, init) =>
      fetch(input, init),
  ) {}

  loadScopes(signal?: AbortSignal): Promise<ChatHomeTilesConfig> {
    return this.requestJson<ChatHomeTilesConfig>(
      "/cms/api/chat-home/scopes",
      signal ? { signal } : {},
    );
  }

  loadTiles(
    scope: Pick<ChatHomeTilesScope, "country" | "product" | "culture">,
    signal?: AbortSignal,
  ): Promise<ChatHomeTilesResponse> {
    const query = new URLSearchParams({
      country: scope.country,
      product: scope.product,
      culture: scope.culture,
    });
    return this.requestJson<ChatHomeTilesResponse>(
      `/cms/api/chat-home/tiles?${query}`,
      signal ? { signal } : {},
    );
  }

  saveTile(
    request: ChatHomeTileSaveRequest,
    antiForgeryToken: string,
  ): Promise<ChatHomeTileCmsTile> {
    return this.requestJson<ChatHomeTileCmsTile>("/cms/api/chat-home/tiles", {
      method: "POST",
      body: JSON.stringify(request),
      antiForgeryToken,
    });
  }

  updateTile(
    id: number,
    request: ChatHomeTileSaveRequest,
    antiForgeryToken: string,
  ): Promise<ChatHomeTileCmsTile> {
    return this.requestJson<ChatHomeTileCmsTile>(
      `/cms/api/chat-home/tiles/${id}`,
      {
        method: "PUT",
        body: JSON.stringify(request),
        antiForgeryToken,
      },
    );
  }

  deleteTile(id: number, antiForgeryToken: string): Promise<void> {
    return this.requestJson<void>(`/cms/api/chat-home/tiles/${id}`, {
      method: "DELETE",
      antiForgeryToken,
    });
  }

  private async requestJson<T>(
    url: string,
    init: RequestInit & { antiForgeryToken?: string } = {},
  ): Promise<T> {
    const { antiForgeryToken, ...requestInit } = init;
    const hasBody = typeof requestInit.body === "string";
    const headers: Record<string, string> = {
      Accept: "application/json",
      "Cache-Control": "no-store",
      Pragma: "no-cache",
      // The Base64RequestBodyMiddleware decodes the body back to JSON before model
      // binding, so the content type stays application/json.
      ...(hasBody
        ? { "Content-Type": "application/json", "X-Body-Encoding": "base64" }
        : {}),
    };

    if (antiForgeryToken) {
      headers.RequestVerificationToken = antiForgeryToken;
    }

    const response = await this.fetchFunction(url, {
      ...requestInit,
      // Wrap write bodies in a Base64 JSON envelope so the AFAS edge WAF does not 403
      // legitimate CMS tile content that contains '<', '>' or SQL-like text (e.g. a menu
      // path like "Beheer > Inrichting"). The envelope stays valid JSON so the WAF treats
      // the Base64 as an opaque string; the server unwraps it before binding.
      ...(hasBody ? { body: encodeRequestBody(requestInit.body as string) } : {}),
      headers,
      credentials: "same-origin",
      cache: "no-store",
    });

    if (!response.ok) {
      throw new ChatHomeTilesApiError(
        `Chat home tiles request failed: ${response.status}`,
        response.status,
        await this.readErrors(response),
      );
    }

    if (response.status === 204) {
      return undefined as T;
    }

    return (await response.json()) as T;
  }

  private async readErrors(response: Response): Promise<string[]> {
    try {
      const body = (await response.json()) as { errors?: unknown };
      if (Array.isArray(body.errors)) {
        return body.errors.filter(
          (error): error is string => typeof error === "string",
        );
      }
    } catch {
      return [];
    }

    return [];
  }
}

// Wraps a UTF-8 request body in a Base64 JSON envelope: `{"payload":"<base64>"}`. The
// AFAS edge WAF (OWASP CRS) inspects request bodies and returns a 403 when it sees '<',
// '>' or SQL-like tokens, which legitimate plain-text CMS tile content can contain. A
// bare Base64 body is not valid JSON, so the WAF base64-decodes it and re-finds those
// bytes; wrapping it in a JSON envelope keeps it valid JSON and the Base64 opaque. The
// Base64RequestBodyMiddleware unwraps the envelope back to JSON before the controller
// binds it. btoa() only accepts Latin1, so the UTF-8 bytes are mapped to a binary string
// first to keep accented characters (é, ï, …) intact.
function encodeRequestBody(body: string): string {
  const bytes = new TextEncoder().encode(body);
  let binary = "";
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return JSON.stringify({ payload: btoa(binary) });
}
